/**
 * Zugriff auf die API.
 *
 * Die API antwortet auf jeden Fehler in derselben Form:
 *   { "fehler": "kennung", "meldung": "Text für Menschen" }
 *
 * Die Kennung ist stabil und für die Oberfläche gedacht — an ihr hängt
 * etwa, ob das TOTP-Feld erscheint. Die Meldung ist für den Benutzer und
 * wird unverändert angezeigt: Sie ist auf der Serverseite bereits deutsch
 * und fachlich formuliert, eine zweite Übersetzung hier würde nur
 * auseinanderlaufen.
 */

export class ApiFehler extends Error {
  constructor(
    readonly status: number,
    readonly kennung: string,
    meldung: string,
    readonly zusatz: Record<string, unknown> = {},
  ) {
    super(meldung);
    this.name = 'ApiFehler';
  }
}

/**
 * Wird gemeldet, wenn die Sitzung nicht mehr gilt.
 *
 * Die Sitzung kann jederzeit ablaufen — nach acht Stunden Untätigkeit oder
 * weil sie auf einem anderen Gerät widerrufen wurde. Jede Komponente
 * einzeln darauf prüfen zu lassen, wäre lückenhaft; stattdessen meldet die
 * API-Schicht es einmal, und der Sitzungskontext schaltet auf die
 * Anmeldemaske um.
 */
export const SITZUNG_ENDE = 'gj:sitzung-abgelaufen';

function meldeSitzungsende(): void {
  window.dispatchEvent(new CustomEvent(SITZUNG_ENDE));
}

type Methode = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

async function anfrage<T>(methode: Methode, pfad: string, rumpf?: unknown): Promise<T> {
  let antwort: Response;
  try {
    antwort = await fetch(pfad, {
      method: methode,
      // Das Sitzungs-Cookie ist HttpOnly; ohne 'same-origin' schickt der
      // Browser es bei fetch() nicht mit.
      credentials: 'same-origin',
      headers: rumpf === undefined ? {} : { 'Content-Type': 'application/json' },
      ...(rumpf === undefined ? {} : { body: JSON.stringify(rumpf) }),
    });
  } catch {
    // Netzwerkfehler, abgebrochene Verbindung, Server nicht erreichbar.
    // Der Unterschied ist für den Benutzer belanglos, die Folge nicht:
    // Es ist nichts gespeichert.
    throw new ApiFehler(
      0,
      'keine_verbindung',
      'Keine Verbindung zum Server. Der Vorgang wurde nicht gespeichert.',
    );
  }

  return auswerten<T>(antwort);
}

async function auswerten<T>(antwort: Response): Promise<T> {
  if (antwort.status === 204) return undefined as T;

  const typ = antwort.headers.get('content-type') ?? '';

  if (!typ.includes('application/json')) {
    if (antwort.ok) return (await antwort.blob()) as T;
    throw new ApiFehler(
      antwort.status,
      'unerwartete_antwort',
      `Der Server antwortete mit ${antwort.status}.`,
    );
  }

  const daten = (await antwort.json()) as Record<string, unknown>;

  if (antwort.ok) return daten as T;

  const kennung = typeof daten['fehler'] === 'string' ? daten['fehler'] : 'unbekannt';
  const meldung =
    typeof daten['meldung'] === 'string' ? daten['meldung'] : 'Unbekannter Fehler.';

  if (antwort.status === 401 && kennung === 'nicht_angemeldet') meldeSitzungsende();

  const { fehler: _f, meldung: _m, ...zusatz } = daten;
  throw new ApiFehler(antwort.status, kennung, meldung, zusatz);
}

export const api = {
  hole: <T>(pfad: string) => anfrage<T>('GET', pfad),
  senden: <T>(pfad: string, rumpf?: unknown) => anfrage<T>('POST', pfad, rumpf ?? {}),
  aendern: <T>(pfad: string, rumpf: unknown) => anfrage<T>('PATCH', pfad, rumpf),
  ersetzen: <T>(pfad: string, rumpf: unknown) => anfrage<T>('PUT', pfad, rumpf),
  entfernen: <T>(pfad: string) => anfrage<T>('DELETE', pfad),

  /**
   * Datei hochladen.
   *
   * Ohne Content-Type-Kopfzeile: Den mehrteiligen Trenner setzt der Browser
   * selbst, und er muss zum Rumpf passen. Wer ihn hier von Hand setzt,
   * bekommt einen 400er, dessen Ursache man lange sucht.
   */
  sendeDatei: async <T>(pfad: string, datei: File, feld = 'datei'): Promise<T> => {
    const formular = new FormData();
    formular.append(feld, datei);
    let antwort: Response;
    try {
      antwort = await fetch(pfad, {
        method: 'POST',
        credentials: 'same-origin',
        body: formular,
      });
    } catch {
      throw new ApiFehler(0, 'keine_verbindung', 'Keine Verbindung zum Server.');
    }
    return auswerten<T>(antwort);
  },
};

/** Fragezeichen-Anhang aus einem Objekt — leere Werte fallen weg. */
export function alsAbfrage(werte: Record<string, string | number | undefined | null>): string {
  const teile = new URLSearchParams();
  for (const [schluessel, wert] of Object.entries(werte)) {
    if (wert === undefined || wert === null || wert === '') continue;
    teile.set(schluessel, String(wert));
  }
  const text = teile.toString();
  return text ? `?${text}` : '';
}

/** Meldung aus einem unbekannten Fehlerobjekt — für catch-Blöcke. */
export function fehlertext(f: unknown): string {
  if (f instanceof ApiFehler) return f.message;
  if (f instanceof Error) return f.message;
  return 'Unbekannter Fehler.';
}
